/**
 * SecureLink 2.0 TypeScript SDK
 *
 */

// ---------------------------------------------------------------------------
// Protocol types
// ---------------------------------------------------------------------------

export interface SL2BaseResponse {
  Command: string;
  Callback: string;
  MessageId: string;
  SessionId: string;
  Copyright?: string;
  HttpRC: string;
  /** '0' = success, '2' = feeder empty, other = error */
  Result: string;
  ErrorInformation: string;
  ExtResult: string;
  ExtInformation: string;
}

export interface SL2ConnectResponse extends SL2BaseResponse {
  ScannerModel: string;
  VersionObject?: {
    Version: string;
    Name: string;
    Product: string;
  };
}

export interface SL2MicrFields {
  /** Varies by scanner — consult Parameters doc */
  Fld1?: string;
  /** Check number */
  Fld2?: string;
  /** Account number */
  Fld3?: string;
  Fld4?: string;
  /** Routing / transit number */
  Fld5?: string;
  Fld6?: string;
  Fld7?: string;
  RawMicr?: string;
}

export interface SL2ImageEntry {
  /** fbw | rbw | fgs | rgs | fclr | rclr | sgs */
  ImageType: string;
  /** g4tiff | jpeg | jpegtiff | bmp */
  ImageFormat: string;
  /** e.g. "240dpi" */
  ImageResolution: string;
  /** base64-encoded image data */
  data: string;
}

export interface SL2GetDocumentResponse extends SL2BaseResponse {
  Device?: { DeviceIPAddress: string; MAC: string };
  Scanner?: { ScannerModel: string; Serial: string };
  CurrentDate?: string;
  CurrentTime?: string;
  SequenceNumber?: string;
  MicrFields: SL2MicrFields;
  OcrFields?: Record<string, string>;
  Images: SL2ImageEntry[];
  DocumentsQueued: string;
}

export interface SL2SetParamsResponse extends SL2BaseResponse {
  Params?: Record<string, unknown>;
}

export interface SL2GetParamsResponse extends SL2BaseResponse {
  Params?: Record<string, unknown>;
}

/** Sentinel returned by getDocument() when the feeder is empty (mirrors SDK's SecureLink2.FeederEmpty) */
export interface SL2FeederEmpty {
  FeederEmpty: 'true';
}

export const SL2_FEEDER_EMPTY: SL2FeederEmpty = { FeederEmpty: 'true' };

export function isSL2FeederEmpty(val: unknown): val is SL2FeederEmpty {
  return typeof val === 'object' && val !== null && 'FeederEmpty' in val;
}

// ---------------------------------------------------------------------------
// Image type constants (mirrors SecureLink2.Images in sl2.js)
// ---------------------------------------------------------------------------

export const SL2Images = {
  blackWhite: { front: 'fbw', rear: 'rbw' },
  grayscale: { front: 'fgs', rear: 'rgs', snippet: 'sgs' },
  color: { front: 'fclr', rear: 'rclr' },
} as const;

// ---------------------------------------------------------------------------
// Connection interface
// ---------------------------------------------------------------------------

export interface SL2Connection {
  disconnect(): Promise<SL2BaseResponse>;
  setParams(params?: Record<string, string>): Promise<SL2SetParamsResponse>;
  getParams(...params: string[]): Promise<SL2GetParamsResponse>;
  getDocument(options?: {
    IncludeImages?: string;
    Timeout?: string;
    [key: string]: unknown;
  }): Promise<SL2GetDocumentResponse | SL2FeederEmpty>;
  getImage(...types: string[]): Promise<Blob[]>;
  getImageURL(...types: string[]): Promise<string[]>;
  clearTrack(): Promise<SL2BaseResponse>;
  stopFlow(options?: Record<string, unknown>): Promise<SL2BaseResponse>;
}

export interface SL2Session {
  connection: SL2Connection;
  connectResponse: SL2ConnectResponse;
}

// ---------------------------------------------------------------------------
// Core factory — mirrors `function SecureLink2(host)` in sl2.js
// ---------------------------------------------------------------------------

export function SecureLink2(host: string) {
  // Mirrors makeAPICallRaw() in sl2.js
  function makeAPICallRaw(commandObj: Record<string, unknown>): Promise<SL2BaseResponse> {
    return fetch(host + '/securelink', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(commandObj),
    }).then((response) =>
      response.json().then((res: SL2BaseResponse) => {
        if ('Result' in res) {
          if (res.Result === '0') return Promise.resolve(res);
          // Result '2' = feeder empty — caller handles as SL2FeederEmpty sentinel
          return Promise.reject({
            HttpRC: res.HttpRC,
            Result: res.Result,
            ErrorInformation: res.ErrorInformation,
            ExtResult: res.ExtResult,
            ExtInformation: res.ExtInformation,
          });
        }
        return Promise.reject(res);
      })
    );
  }

  // Mirrors randomMessageId() in sl2.js
  function randomMessageId(): string {
    const min = 100000000;
    const max = 999999999;
    return Math.floor(Math.random() * (max - min + 1) + min).toString();
  }

  // Mirrors makeAPICall() in sl2.js
  function makeAPICall(
    command: string,
    sessionId: string | null,
    options?: Record<string, unknown>
  ): Promise<SL2BaseResponse> {
    const baseCommand: Record<string, unknown> = {
      Command: command,
      Callback: 'sl2tsCallback',
      MessageId: randomMessageId(),
    };
    if (sessionId != null) baseCommand['SessionId'] = sessionId;
    const merged = { ...baseCommand, ...(options ?? {}) };
    return makeAPICallRaw(merged);
  }

  // Mirrors getImageURL() in sl2.js
  function getImageURL(type: string): string {
    return host + '/sl20/' + type + '_image';
  }

  // Mirrors getArrayFromArgs() in sl2.js
  function getArrayFromArgs(args: string[]): string[] {
    if (args.length === 1 && Array.isArray(args[0])) return args[0];
    return args;
  }

  return {
    // Mirrors scanner.connect() in sl2.js
    connect(connectOptions?: Record<string, unknown>): Promise<SL2Session> {
      return makeAPICall('Connect', null, connectOptions).then((connectResponse) => {
        const sessionId = (connectResponse as SL2ConnectResponse).SessionId;

        function apiCall(command: string, options?: Record<string, unknown>): Promise<SL2BaseResponse> {
          return makeAPICall(command, sessionId, options);
        }

        const connection: SL2Connection = {
          disconnect: () => apiCall('Disconnect'),

          setParams: (params?: Record<string, string>) => apiCall('SetParameters', { Params: params ?? {} }),

          getParams: (...params: string[]) => {
            params = getArrayFromArgs(params);
            return apiCall('GetParameters', { Params: params ?? [] });
          },

          // Mirrors connection.getDocument() in sl2.js — returns FeederEmpty sentinel on Result=2
          getDocument: (options?) =>
            apiCall('GetDocument', options).catch((err) => {
              if ('Result' in err && err.Result === '2') {
                return Promise.resolve(SL2_FEEDER_EMPTY);
              }
              return Promise.reject(err);
            }) as Promise<SL2GetDocumentResponse | SL2FeederEmpty>,

          getImage: (...types: string[]) => {
            types = getArrayFromArgs(types);
            return Promise.all(types.map((x) => fetch(getImageURL(x)).then((res) => res.blob())));
          },

          getImageURL: (...types: string[]) => {
            types = getArrayFromArgs(types);
            return Promise.resolve(types.map((x) => getImageURL(x)));
          },

          clearTrack: () => apiCall('ClearTrack'),

          stopFlow: (options?) => apiCall('StopFlow', options),
        };

        return { connection, connectResponse: connectResponse as SL2ConnectResponse };
      });
    },
  };
}

// ---------------------------------------------------------------------------
// Static helpers — mirror SecureLink2.getIncludedImageDataURLs() in sl2.js
// ---------------------------------------------------------------------------

/** Build data URLs from base64 images embedded in a GetDocument response. */
export function getIncludedImageDataURLs(images: SL2ImageEntry[], ...types: string[]): string[] {
  if (types.length === 1 && Array.isArray(types[0])) types = types[0] as string[];

  function buildDataURL(img: SL2ImageEntry): string {
    let mime: string;
    switch (img.ImageFormat) {
      case 'g4tiff':
      case 'jpegtiff':
        mime = 'tiff';
        break;
      case 'jpeg':
        mime = 'jpeg';
        break;
      case 'bmp':
        mime = 'bmp';
        break;
      default:
        mime = 'jpeg';
    }
    return `data:image/${mime};base64,${img.data}`;
  }

  const typeMap: Record<string, number> = {};
  const result: string[] = new Array(types.length).fill('');
  types.forEach((t, i) => (typeMap[t] = i));

  for (const img of images) {
    if (img.ImageType in typeMap) {
      result[typeMap[img.ImageType]] = buildDataURL(img);
    }
  }

  return result;
}

/** Build object URLs from Blob images returned by getImage(). */
export function getImagesAsDataURLs(images: Blob[]): string[] {
  return images.map((img) => URL.createObjectURL(img));
}
